import { AppPaths } from "@p0security/shared/routes/constants";
import * as Sentry from "@sentry/react";
import { Button, Layout, Result } from "antd";
import { AssessmentRoutes } from "components/Assessment/Routes";
import { useNavigateWithEnv } from "components/Assessment/hooks/useNavigateWithEnv";
import CreateAccountPage from "components/CreateAccount/v2/CreateAccountPage";
import { EnvironmentDocsProvider } from "components/Environment/contexts/EnvironmentDocsContext";
import ContactSupport from "components/Integrations/components/ContactSupport";
import { JitRoutes } from "components/Jit/Routes";
import { OidcRedirect } from "components/Login/pkce";
import { PoliciesRoutes } from "components/Policies/Routes";
import SessionManager from "components/Session/SessionManager";
import { WelcomeRoutes } from "components/Welcome/Routes";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useCallback, useMemo } from "react";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import { useLocation, useParams } from "react-router";
import { Outlet, Route, Routes } from "react-router-dom";
import styled from "styled-components";

import { PolicyRoutes } from "../../policies/Routes";
import { Admin } from "../Admin/Admin";
import { GithubSetupRedirect } from "../Integrations/Github/GithubSetupRedirect";
import { OAuth2Redirect } from "../Integrations/OAuth2";
import { IntegrationRoutes } from "../Integrations/Routes";
import {
  AuthzGuard,
  DeviceAuth,
  EmailCallbackHandler,
  EmailVerified,
  Login,
  OrganizationSelect,
  PasswordChanged,
} from "../Login";
import { useUser } from "../Login/hook";
import UserAuthProvider from "../Login/providers/UserAuthProvider";
import {
  CustomOidcCallbackScreen,
  CustomOidcDebugScreen,
  OidcPopupCallbackScreen,
} from "../Login/routes/CustomOidcScreens";
import { SandboxAlert, sandboxRoutes } from "../Sandbox/Routes";
import { VaultRoutes } from "../Vault/Routes.tsx";
import "./App.less";
import { Footer } from "./Footer";
import { Navbar } from "./Navbar";
import { OnboardingRedirect } from "./OnboardingRedirect";
import { Page } from "./Page";
import { DisabledTenant } from "./pages/DisabledTenant";
import { NotFound } from "./pages/NotFound";
import { ENVIRONMENT_ROLES } from "./roles";

const { Content } = Layout;

const DisabledTenantRedirect: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { tenant } = useUser();
  const { orgSlug } = useParams();
  return tenant.state === "found" && tenant.disabled ? (
    <DisabledTenant orgSlug={orgSlug} />
  ) : (
    <>{children}</>
  );
};

const StyledWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100vw;
`;

const ErrorFallback: React.FC<FallbackProps> = ({
  resetErrorBoundary,
  error,
}) => {
  const navigate = useNavigateWithEnv();
  const { pathname } = useLocation();

  const currentPath = useMemo(() => {
    const segments = pathname.split("/");
    const path = segments.length >= 4 ? segments[3] : AppPaths.Dashboard;
    return Object.values(AppPaths).find((p) => p === path);
  }, [pathname]);

  Sentry.captureException(error);

  const handleBack = useCallback(() => {
    resetErrorBoundary();
    navigate(currentPath ?? AppPaths.Dashboard, {
      replace: true,
    });
  }, [navigate, resetErrorBoundary, currentPath]);

  const handleBackToDashboard = useCallback(() => {
    resetErrorBoundary();
    navigate(AppPaths.Dashboard, {
      replace: true,
    });
  }, [navigate, resetErrorBoundary]);

  return (
    <StyledWrapper>
      <Result
        status="500"
        title="500"
        subTitle={
          <>
            Sorry, something went wrong. If the problem persists, please
            <ContactSupport subject={"Error Page - Issue Description"} />.
          </>
        }
        extra={
          <>
            <Button type="primary" onClick={handleBack}>
              Go Back
            </Button>
            <Button type="default" onClick={handleBackToDashboard}>
              Go to Dashboard
            </Button>
          </>
        }
      />
    </StyledWrapper>
  );
};

const AppLayout: React.FC<object> = () => {
  const { tenant } = useUser();
  const { pathname } = useLocation();
  return (
    <Layout>
      <SessionManager>
        <EnvironmentDocsProvider>
          <Navbar />
          <ErrorBoundary
            FallbackComponent={ErrorFallback}
            resetKeys={[pathname]}
          >
            <Layout className="site-layout">
              <Content className="site-layout-background">
                {tenant.state === "found" && tenant.isSandbox && (
                  <SandboxAlert />
                )}
                <Outlet />
                <Footer />
              </Content>
            </Layout>
          </ErrorBoundary>
        </EnvironmentDocsProvider>
      </SessionManager>
    </Layout>
  );
};

export const App: React.FC = () => {
  const flags = useFlags();

  return (
    <Routes>
      {PolicyRoutes}
      <Route path="/integration/auth/_redirect" element={<OAuth2Redirect />} />
      <Route
        path="/integration/github/_redirect"
        element={<GithubSetupRedirect />}
      />
      <Route path="/oidc/auth/_redirect" element={<OidcRedirect />} />
      <Route path="/oidc/callback" element={<CustomOidcCallbackScreen />} />
      <Route path="/oidc/debug" element={<CustomOidcDebugScreen />} />
      <Route
        path="/oidc/popup-callback"
        element={<OidcPopupCallbackScreen />}
      />
      {/* Allow signups is an environment-level flag. Does not make sense to set it per-tenant since this endpoint is public and outside the scope of a logged-in user */}
      {flags.allowSignups && (
        <Route path="/create-account" element={<CreateAccountPage />} />
      )}
      <Route
        path="/login/email-callback"
        element={
          <UserAuthProvider>
            <EmailCallbackHandler />
          </UserAuthProvider>
        }
      />
      <Route path="/login/email-verified" element={<EmailVerified />} />
      <Route path="/login/password-changed" element={<PasswordChanged />} />
      <Route
        path="/o/:orgSlug/authorize/cli"
        element={
          <Login>
            <DeviceAuth />
          </Login>
        }
      />
      {WelcomeRoutes()}
      <Route
        path="/o/:orgSlug/"
        element={
          <Login>
            <OnboardingRedirect>
              <DisabledTenantRedirect>
                <AppLayout />
              </DisabledTenantRedirect>
            </OnboardingRedirect>
          </Login>
        }
      >
        <Route element={<Outlet key={Date.now()} />}>
          <Route
            index
            element={
              <AuthzGuard
                requirement={ENVIRONMENT_ROLES}
                redirectAuthz={AppPaths.Dashboard}
                redirectNoAuthz={AppPaths.AccessManagement}
              />
            }
          />
          <Route element={<AuthzGuard requirement={ENVIRONMENT_ROLES} />}>
            {AssessmentRoutes}
          </Route>
        </Route>
        {JitRoutes()}
        {PoliciesRoutes}
        {VaultRoutes}
        {sandboxRoutes()}
        <Route element={<AuthzGuard requirement={["owner"]} />}>
          <Route
            path={AppPaths.P0Admin}
            element={
              <Page title="P0 Management">
                <Admin />
              </Page>
            }
          />
        </Route>
        <Route element={<AuthzGuard requirement={["approver", "owner"]} />}>
          {IntegrationRoutes()}
        </Route>
        <Route path="*" element={<NotFound />} />
      </Route>
      <Route
        path="*"
        element={
          <Layout>
            <OrganizationSelect />
          </Layout>
        }
      />
    </Routes>
  );
};
